import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Tooltip } from "../Tooltip";
import PrereqTooltipContent from "../Tooltip/PrereqTooltipContent";
import { useTranslation } from "react-i18next";
import { courseTypes } from "../../constants";
import { displayJoinButton } from "../../utils/course_utils";
import { isBeforeDate } from "../../utils/datetime";

const useStyles = makeStyles(theme => ({
  button: {
    flex: 1
  }
}));

const getTooltipTitle = (t, isAtCapacity, isInstructorForCourse, prereqs) => {
  if (prereqs) {
    return <PrereqTooltipContent prereqName={prereqs} />;
  } else if (isInstructorForCourse) {
    return (
      <Typography variant="body2" color="textPrimary">
        {t("components.tooltip.IltInstructorMessage")}
      </Typography>
    );
  } else if (isAtCapacity) {
    return (
      <Typography variant="body2" color="textPrimary">
        {t("components.tooltip.isAtCapacity")}
      </Typography>
    );
  } else {
    return "";
  }
};

const CourseActionButton = ({
  prereqs,
  onEnrollClick,
  onResumeCourseClick,
  onAddToCoursesClick,
  courseType,
  isEnrolled,
  isAssigned,
  isStarted,
  isInstructorForCourse,
  hasNoFreeSeats,
  startDate,
  endDate,
  iltClassType,
  isForUpcomingIltWidget,
  manualId,
  assignedTrainingObjectId,
  ...buttonProps
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const getButtonText = () => {
    if (courseType === courseTypes.policy) {
      return t("courses.progressBtns.view");
    }
    if (!isEnrolled) {
      return t("courses.progressBtns.play", {
        context: courseType?.toLowerCase()
      });
    } else if (
      isForUpcomingIltWidget &&
      iltClassType?.toLowerCase() === courseTypes.ilt.toLowerCase() &&
      isBeforeDate(Date.now(), startDate)
    ) {
      // Show "Get Directions" for the button text in the upcoming ILT widget
      // if the current date is before the start date of the session.
      return t("getDirections");
    } else if (
      iltClassType?.toLowerCase() !== courseTypes.ilt.toLowerCase() &&
      displayJoinButton(startDate, endDate)
    ) {
      return t("courses.progressBtns.join");
    } else {
      return t("courses.progressBtns.play");
    }
  };

  const handlePolicyClick = () => {
    // Send message to PHQ to route user to policy viewer
    window.parent.postMessage(
      {
        action: "switchRoute",
        route: `/manuals/${manualId}/policies/${assignedTrainingObjectId}`
      },
      "*"
    );
  };

  const handleClick = () => {
    if (
      courseType === courseTypes.policy &&
      assignedTrainingObjectId &&
      manualId
    ) {
      handlePolicyClick();
    } else if (isStarted && isEnrolled) {
      onResumeCourseClick();
    } else {
      onEnrollClick();
    }
  };

  const isAtCapacity = !isEnrolled && hasNoFreeSeats;

  const needsPrereq =
    (isEnrolled ||
      (isAssigned &&
        courseType?.toLowerCase() !== courseTypes.ilt.toLowerCase())) &&
    !!prereqs?.length;

  let button = null;

  if (!isAssigned && !isEnrolled) {
    button = (
      <Button
        variant="outlined"
        color="primary"
        disabled={isInstructorForCourse || isAtCapacity}
        onClick={onAddToCoursesClick}
        className={classes.button}
        {...buttonProps}
      >
        {t("library.saveBtn")}
      </Button>
    );
  } else {
    button = (
      <Button
        variant="contained"
        color="primary"
        className={classes.button}
        onClick={handleClick}
        disabled={needsPrereq || isAtCapacity || isInstructorForCourse}
        data-qa="course-action-button"
        {...buttonProps}
      >
        {getButtonText()}
      </Button>
    );
  }
  return (
    <Tooltip
      title={getTooltipTitle(
        t,
        isAtCapacity,
        isInstructorForCourse,
        needsPrereq ? prereqs : null
      )}
    >
      <Box display="flex" flex={1}>
        {button}
      </Box>
    </Tooltip>
  );
};

export default CourseActionButton;

CourseActionButton.propTypes = {
  prereqs: PropTypes.arrayOf(PropTypes.string),
  courseType: PropTypes.oneOf([
    ...Object.values(courseTypes),
    ...Object.values(courseTypes).map(type => type.toLowerCase())
  ]),
  onClick: PropTypes.func,
  isEnrolled: PropTypes.bool,
  isAssigned: PropTypes.bool,
  onAddToCoursesClick: PropTypes.func,
  hasNoFreeSeats: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  iltClassType: PropTypes.string,
  isForUpcomingIltWidget: PropTypes.bool
};

CourseActionButton.defaultProps = {
  isAssigned: true
};
